import React, { useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, type GridRenderCellParams } from '@mui/x-data-grid-pro';

import {
  type MarketingRouterTreatmentType,
  checkMarketingRouter,
  getMarketingRouterTreatmentConfig,
  marketingRouterTreatmentTypes,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import PageHeader from '~/components/Common/Elements/PageHeader';

import {
  useCreateMarketingRouter,
  useMarketingRouterRules,
  useUpdateMarketingRouter,
} from '~/hooks/apis/promo/useMarketingRouterRules';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { MarketingRouterCreatePayload, MarketingRouterInternalQuery } from '~/services/PromoService';

import {
  type MarketingRouterRule,
  type MarketingRouterRuleSignificanceMetric,
  getMarketingRouterRuleCookieName,
} from '~/types/marketingRouterRule';

import { normalizeToISODate } from '~/utils/dateFormatter';

import {
  type MarketingRouterTreatmentDraft,
  type MarketingRouterTreatmentFieldName,
  getPersistedTreatmentValue,
  getTreatmentFieldValueForDraft,
  normalizeTreatmentForType,
} from './marketingRouterTreatments';

type RouteParams = { ruleId?: string };
type Treatment = MarketingRouterTreatmentDraft;
type Variant = Omit<MarketingRouterRule['variants'][number], 'treatments'> & { treatments?: Array<Treatment> };
type RuleFormData = Omit<MarketingRouterRule, 'variants'> & { variants: Array<Variant> };
type TreatmentRow = Treatment & { id: string };

const SIGNIFICANCE_METRICS: Array<{ value: MarketingRouterRuleSignificanceMetric; label: string }> = [
  { value: 'ttv', label: 'TTV' },
  { value: 'margin', label: 'Margin' },
  { value: 'purchase', label: 'Conversion' },
];
const DEFAULT_TREATMENT_TYPE: MarketingRouterTreatmentType = 'promo-popup';

function getTreatmentSummary(treatment: Treatment) {
  const config = getMarketingRouterTreatmentConfig(treatment.type);
  const fields =
    config?.fields
      ?.map((field) => {
        const value = getTreatmentFieldValueForDraft(treatment, field.field);
        return value ? `${field.fieldName}: ${value}` : null;
      })
      .filter(Boolean) ?? [];

  return {
    config,
    fields,
  };
}

function createDefaultTreatment(): Treatment {
  return { type: DEFAULT_TREATMENT_TYPE };
}

function appendTreatment(variant: Variant): Variant {
  const treatments = variant.treatments ?? [];

  return {
    ...variant,
    treatments: [...treatments, createDefaultTreatment()],
  };
}

function omitTreatments(variant: Variant): Variant {
  const { treatments: _treatments, ...variantWithoutTreatments } = variant;
  return variantWithoutTreatments;
}

const emptyRule: RuleFormData = {
  id: '',
  name: '',
  significance_metrics: ['ttv'],
  start_date: '',
  end_date: '',
  match: { utm_source: '', utm_medium: '', utm_campaign: '' },
  variants: [
    {
      variant_id: '',
      variant_name: '',
      weight: 100,
      is_control: true,
    },
  ],
};

function hasAdvancedMatchConditions(match: MarketingRouterRule['match']): boolean {
  return Boolean(match.utm_term || match.utm_content || match.url);
}

// The rule records returned by useMarketingRouterRules carry router-level
// context (marketingRouterId, brand, ruleJson, …). Only the canonical rule
// fields may enter form state — svc-promo rejects anything else on save.
function toRuleFormData(rule: RuleFormData): RuleFormData {
  return {
    id: rule.id,
    name: rule.name,
    significance_metrics: rule.significance_metrics,
    start_date: rule.start_date,
    end_date: rule.end_date,
    match: rule.match,
    variants: rule.variants,
  };
}

function prepareRule(rule: RuleFormData, id: string, generateVariantIds = true): MarketingRouterRule {
  return {
    ...toRuleFormData(rule),
    id,
    name: rule.name.trim(),
    start_date: normalizeToISODate(rule.start_date),
    end_date: normalizeToISODate(rule.end_date),
    match: {
      utm_source: rule.match.utm_source.trim(),
      utm_medium: rule.match.utm_medium.trim(),
      utm_campaign: rule.match.utm_campaign.trim(),
      ...(rule.match.utm_term?.trim() ? { utm_term: rule.match.utm_term.trim() } : {}),
      ...(rule.match.utm_content?.trim() ? { utm_content: rule.match.utm_content.trim() } : {}),
      ...(rule.match.url?.trim() ? { url: rule.match.url.trim() } : {}),
    },
    variants: rule.variants.map((variants) => {
      const { treatments, ...variant } = variants;
      const preparedVariant = {
        ...variant,
        variant_id: variant.variant_id || (generateVariantIds ? globalThis.crypto.randomUUID() : ''),
        variant_name: variant.variant_name.trim(),
      };

      return treatments
        ? {
            ...preparedVariant,
            treatments: treatments.map(getPersistedTreatmentValue),
          }
        : preparedVariant;
    }),
  };
}

export default function MarketingRouterRuleFormPage() {
  const navigate = useNavigate();
  const { ruleId } = useParams<RouteParams>();
  const { tenant } = useCurrentTenant();
  const query: MarketingRouterInternalQuery = {
    brand: tenant.brand as MarketingRouterInternalQuery['brand'],
    page: 0,
    pageSize: 100,
  };
  const rulesQuery = useMarketingRouterRules({ query });
  const rules = useMemo(() => rulesQuery.data ?? [], [rulesQuery.data]);
  const existingRule = useMemo(() => rules.find((rule) => rule.id === ruleId), [ruleId, rules]);
  const createMarketingRouterMutation = useCreateMarketingRouter();
  const updateMarketingRouterMutation = useUpdateMarketingRouter();
  const [rule, setRule] = useState<RuleFormData>(() => (existingRule ? toRuleFormData(existingRule) : emptyRule));
  const [expandedVariantIndices, setExpandedVariantIndices] = useState<Array<number>>([]);
  const [expandedTreatmentRowIds, setExpandedTreatmentRowIds] = useState<Record<number, Array<string>>>({});
  const [isAdvancedMatchVisible, setIsAdvancedMatchVisible] = useState(() =>
    hasAdvancedMatchConditions(existingRule?.match ?? emptyRule.match),
  );
  const [showFieldDescriptions, setShowFieldDescriptions] = useState(false);
  const [errors, setErrors] = useState<Array<string>>([]);
  const isEditing = Boolean(ruleId);
  const isSaving = createMarketingRouterMutation.isPending || updateMarketingRouterMutation.isPending;

  useEffect(() => {
    if (existingRule) {
      setRule(toRuleFormData(existingRule));
      setIsAdvancedMatchVisible(hasAdvancedMatchConditions(existingRule.match));
    }
  }, [existingRule]);
  const variantsTotal = rule.variants.reduce((total, variants) => total + variants.weight, 0);
  const hasInvalidVariantTotal = variantsTotal !== 100;
  const currentRule = useMemo(() => prepareRule(rule, rule.id || 'new-marketing-router-rule', false), [rule]);
  const currentRuleCheck = useMemo(() => checkMarketingRouter({ rule_json: [currentRule] }), [currentRule]);
  const currentRuleErrors = useMemo(
    () =>
      Array.from(
        new Set(
          currentRuleCheck.warnings.filter((warning) => warning.level === 'error').map((warning) => warning.message),
        ),
      ),
    [currentRuleCheck.warnings],
  );

  const updateMatch = (field: keyof MarketingRouterRule['match'], value: string) => {
    setRule((currentRule) => ({ ...currentRule, match: { ...currentRule.match, [field]: value } }));
  };

  const updateAdvancedMatchVisibility = (isVisible: boolean) => {
    setIsAdvancedMatchVisible(isVisible);

    if (!isVisible) {
      setRule((currentRule) => {
        const { utm_term: _utmTerm, utm_content: _utmContent, url: _url, ...match } = currentRule.match;

        return { ...currentRule, match };
      });
    }
  };

  const updateVariant = (index: number, field: keyof Variant, value: string) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variants, variantsIndex) =>
        variantsIndex === index ? { ...variants, [field]: field === 'weight' ? Number(value) : value } : variants,
      ),
    }));
  };

  const addVariant = () => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: [
        ...currentRule.variants,
        {
          variant_id: '',
          variant_name: '',
          weight: 0,
          is_control: false,
        },
      ],
    }));
  };

  const addTreatment = (variantIndex: number) => {
    const nextTreatmentIndex = rule.variants[variantIndex]?.treatments?.length ?? 0;
    const nextTreatmentId = String(nextTreatmentIndex);

    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variant, currentVariantIndex) =>
        currentVariantIndex === variantIndex ? appendTreatment(variant) : variant,
      ),
    }));
    setExpandedTreatmentRowIds((currentExpandedTreatmentRowIds) => ({
      ...currentExpandedTreatmentRowIds,
      [variantIndex]: [nextTreatmentId],
    }));
    setExpandedVariantIndices((currentExpandedVariantIndices) =>
      currentExpandedVariantIndices.includes(variantIndex)
        ? currentExpandedVariantIndices
        : [...currentExpandedVariantIndices, variantIndex],
    );
  };

  const handleExpandedTreatmentRowIdsChange = (variantIndex: number, rowIds: Array<string | number>) => {
    setExpandedTreatmentRowIds((currentExpandedTreatmentRowIds) => ({
      ...currentExpandedTreatmentRowIds,
      [variantIndex]: rowIds.map(String),
    }));
  };

  const toggleVariantTreatments = (variantIndex: number) => {
    setExpandedVariantIndices((currentExpandedVariantIndices) =>
      currentExpandedVariantIndices.includes(variantIndex)
        ? currentExpandedVariantIndices.filter((currentVariantIndex) => currentVariantIndex !== variantIndex)
        : [...currentExpandedVariantIndices, variantIndex],
    );
  };

  const updateTreatmentType = (variantIndex: number, treatmentIndex: number, type: MarketingRouterTreatmentType) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variant, currentVariantIndex) =>
        currentVariantIndex === variantIndex
          ? {
              ...variant,
              treatments: variant.treatments?.map((treatment, currentTreatmentIndex) =>
                currentTreatmentIndex === treatmentIndex ? normalizeTreatmentForType(treatment, type) : treatment,
              ),
            }
          : variant,
      ),
    }));
  };

  const updateTreatmentField = (
    variantIndex: number,
    treatmentIndex: number,
    field: MarketingRouterTreatmentFieldName,
    value: string,
  ) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variant, currentVariantIndex) =>
        currentVariantIndex === variantIndex
          ? {
              ...variant,
              treatments: variant.treatments?.map((treatment, currentTreatmentIndex) =>
                currentTreatmentIndex === treatmentIndex ? { ...treatment, [field]: value } : treatment,
              ),
            }
          : variant,
      ),
    }));
  };

  const removeTreatment = (variantIndex: number, treatmentIndex: number) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variant, currentVariantIndex) => {
        if (currentVariantIndex !== variantIndex) {
          return variant;
        }

        const treatments = variant.treatments?.filter((_, index) => index !== treatmentIndex) ?? [];

        return treatments.length ? { ...variant, treatments } : omitTreatments(variant);
      }),
    }));
  };

  const removeVariant = (index: number) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.filter((_, variantsIndex) => variantsIndex !== index),
    }));
  };

  const setControlVariant = (index: number) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variants, variantsIndex) => ({
        ...variants,
        is_control: variantsIndex === index,
      })),
    }));
  };

  const toggleSignificanceMetric = (metric: MarketingRouterRuleSignificanceMetric) => {
    setRule((currentRule) => ({
      ...currentRule,
      significance_metrics: currentRule.significance_metrics.includes(metric)
        ? currentRule.significance_metrics.filter((selectedMetric) => selectedMetric !== metric)
        : [...currentRule.significance_metrics, metric],
    }));
  };

  const moveSignificanceMetric = (index: number, direction: -1 | 1) => {
    setRule((currentRule) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= currentRule.significance_metrics.length) {
        return currentRule;
      }

      const metrics = [...currentRule.significance_metrics];
      [metrics[index], metrics[nextIndex]] = [metrics[nextIndex], metrics[index]];
      return { ...currentRule, significance_metrics: metrics };
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors([]);
    const ruleId = rule.id || globalThis.crypto.randomUUID();

    const preparedRule = prepareRule(rule, ruleId);
    const preparedRuleCheck = checkMarketingRouter({ rule_json: [preparedRule] });

    if (!preparedRuleCheck.canSave) {
      return;
    }

    try {
      if (isEditing && existingRule) {
        const ruleJson = existingRule.ruleJson.map((candidate) => ({
          ...candidate,
          start_date:
            candidate.id === preparedRule.id ? preparedRule.start_date : normalizeToISODate(candidate.start_date),
          end_date: candidate.id === preparedRule.id ? preparedRule.end_date : normalizeToISODate(candidate.end_date),
          ...(candidate.id === preparedRule.id ? preparedRule : {}),
        }));
        await updateMarketingRouterMutation.mutateAsync({
          marketingRouterId: existingRule.marketingRouterId,
          brand: existingRule.brand as MarketingRouterCreatePayload['brand'],
          region: existingRule.region as MarketingRouterCreatePayload['region'],
          startDate: preparedRule.start_date,
          endDate: preparedRule.end_date,
          ruleJson,
        });
      } else {
        await createMarketingRouterMutation.mutateAsync({
          brand: tenant.brand as MarketingRouterCreatePayload['brand'],
          startDate: preparedRule.start_date,
          endDate: preparedRule.end_date,
          ruleJson: [preparedRule],
        });
      }
      navigate('/marketing/marketing-router-rules');
    } catch (mutationError) {
      setErrors([mutationError instanceof Error ? mutationError.message : 'Failed to save marketing router rule.']);
    }
  };

  if (isEditing && rulesQuery.isLoading) {
    return (
      <Container maxWidth="xl">
        <Typography color="text.secondary">Loading marketing router rule…</Typography>
      </Container>
    );
  }

  if (isEditing && rulesQuery.isError) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">
          {rulesQuery.error instanceof Error ? rulesQuery.error.message : 'Failed to load marketing router rule.'}
        </Alert>
      </Container>
    );
  }

  if (isEditing && !existingRule) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Marketing Router Rule “{ruleId}” was not found.</Alert>
        <Button component={Link} to="/marketing/marketing-router-rules" variant="text" sx={{ mt: 2 }}>
          Back to rules
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditing ? 'Marketing / Edit Router Rule' : 'Marketing / Create Router Rule'}</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title={isEditing ? `Edit Marketing Router Rule: ${rule.id}` : 'Create Marketing Router Rule'}>
          <Tooltip title={showFieldDescriptions ? 'Hide field descriptions' : 'Show field descriptions'}>
            <IconButton
              color={showFieldDescriptions ? 'primary' : 'default'}
              aria-label={showFieldDescriptions ? 'Hide field descriptions' : 'Show field descriptions'}
              aria-pressed={showFieldDescriptions}
              onClick={() => setShowFieldDescriptions((current) => !current)}
              size="small"
              sx={{ border: 1, borderColor: 'divider' }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </PageHeader>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={3}>
            {errors.length ? (
              <Alert severity="error">
                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                  {errors.map((error, index) => (
                    <Box component="li" key={`${error}-${index}`}>
                      {error}
                    </Box>
                  ))}
                </Box>
              </Alert>
            ) : null}
            <Stack spacing={2}>
              <Typography variant="h6">Rule details</Typography>
              {isEditing ? (
                <TextField label="Rule ID" value={rule.id} disabled />
              ) : (
                <Typography color="text.secondary" variant="body2">
                  Rule ID will be generated automatically when the rule is saved.
                </Typography>
              )}
              <TextField
                label="Rule name"
                required
                fullWidth
                value={rule.name}
                onChange={(event) => setRule((current) => ({ ...current, name: event.target.value }))}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Start date"
                  required
                  type="date"
                  fullWidth
                  value={rule.start_date}
                  onChange={(event) => setRule((current) => ({ ...current, start_date: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End date"
                  required
                  type="date"
                  fullWidth
                  value={rule.end_date}
                  onChange={(event) => setRule((current) => ({ ...current, end_date: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Stack>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Match</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isAdvancedMatchVisible}
                      onChange={(_, checked) => updateAdvancedMatchVisibility(checked)}
                    />
                  }
                  label="Advanced"
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="UTM source"
                  required
                  fullWidth
                  value={rule.match.utm_source}
                  onChange={(event) => updateMatch('utm_source', event.target.value)}
                />
                <TextField
                  label="UTM medium"
                  required
                  fullWidth
                  value={rule.match.utm_medium}
                  onChange={(event) => updateMatch('utm_medium', event.target.value)}
                />
                <TextField
                  label="UTM campaign"
                  required
                  fullWidth
                  value={rule.match.utm_campaign}
                  onChange={(event) => updateMatch('utm_campaign', event.target.value)}
                />
              </Stack>
              {isAdvancedMatchVisible ? (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="UTM term"
                    fullWidth
                    value={rule.match.utm_term ?? ''}
                    onChange={(event) => updateMatch('utm_term', event.target.value)}
                  />
                  <TextField
                    label="UTM content"
                    fullWidth
                    value={rule.match.utm_content ?? ''}
                    onChange={(event) => updateMatch('utm_content', event.target.value)}
                  />
                  <TextField
                    label="URL"
                    fullWidth
                    value={rule.match.url ?? ''}
                    onChange={(event) => updateMatch('url', event.target.value)}
                  />
                </Stack>
              ) : null}
            </Stack>
            <Stack spacing={2}>
              <Typography variant="h6">Metrics</Typography>
              <Typography color="text.secondary" variant="body2">
                Select the metrics used to measure statistical significance. The selected order is used in calculations.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {SIGNIFICANCE_METRICS.map((metric) => (
                  <FormControlLabel
                    key={metric.value}
                    control={
                      <Checkbox
                        checked={rule.significance_metrics.includes(metric.value)}
                        onChange={() => toggleSignificanceMetric(metric.value)}
                      />
                    }
                    label={metric.label}
                  />
                ))}
              </Stack>
              <Stack spacing={1}>
                {rule.significance_metrics.map((metric, index) => (
                  <Stack key={metric} direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ minWidth: 24 }}>{index + 1}.</Typography>
                    <Typography sx={{ minWidth: 120 }}>
                      {SIGNIFICANCE_METRICS.find((option) => option.value === metric)?.label}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={`Move ${metric} up`}
                      onClick={() => moveSignificanceMetric(index, -1)}
                      disabled={index === 0}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Move ${metric} down`}
                      onClick={() => moveSignificanceMetric(index, 1)}
                      disabled={index === rule.significance_metrics.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </Stack>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Variant</Typography>
                <Button type="button" variant="text" onClick={addVariant}>
                  Add variant
                </Button>
              </Stack>
              {hasInvalidVariantTotal ? (
                <Alert severity="error">Variant weights must total 100%. Current total: {variantsTotal}%.</Alert>
              ) : null}
              <TableContainer sx={{ border: 1, borderColor: 'divider' }}>
                <Table size="small" sx={{ minWidth: 1_000 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Variant name</TableCell>
                      <TableCell>Weight (%)</TableCell>
                      <TableCell>Control</TableCell>
                      <TableCell>Cookie</TableCell>
                      <TableCell>Treatments</TableCell>
                      <TableCell aria-label="Actions" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rule.variants.map((variants, index) => (
                      <React.Fragment key={`${variants.variant_id}-${index}`}>
                        <TableRow>
                          <TableCell sx={{ minWidth: 220 }}>
                            <TextField
                              label="Variant name"
                              required
                              fullWidth
                              value={variants.variant_name}
                              onChange={(event) => updateVariant(index, 'variant_name', event.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 130 }}>
                            <TextField
                              label="Weight (%)"
                              required
                              type="number"
                              inputProps={{ min: 0, max: 100, step: 1 }}
                              value={variants.weight}
                              onChange={(event) => updateVariant(index, 'weight', event.target.value)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={variants.is_control}
                              onChange={() => setControlVariant(index)}
                              inputProps={{ 'aria-label': `Set variant ${index + 1} as control` }}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 190 }}>
                            <Typography variant="body2" color="text.secondary">
                              {getMarketingRouterRuleCookieName(
                                rule.id || 'rule_id',
                                variants.variant_id || 'variant_id',
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 360, p: 0 }}>
                            <Stack spacing={0.5} sx={{ width: '100%', mr: 2 }}>
                              {variants.treatments?.length ? (
                                <Stack spacing={0.5}>
                                  {variants.treatments.map((treatment, treatmentIndex) => {
                                    const config = getMarketingRouterTreatmentConfig(treatment.type);
                                    const fields = config?.fields
                                      ?.map((field) => {
                                        const value = getTreatmentFieldValueForDraft(treatment, field.field);
                                        return value ? `${field.fieldName}: ${value}` : null;
                                      })
                                      .filter(Boolean);

                                    return (
                                      <Typography key={`${treatment.type}-${treatmentIndex}`} variant="body2">
                                        {config?.typeName ?? treatment.type}
                                        {fields?.length ? ` — ${fields.join(', ')}` : ''}
                                      </Typography>
                                    );
                                  })}
                                </Stack>
                              ) : (
                                <Typography color="text.secondary" variant="body2">
                                  No treatments configured
                                </Typography>
                              )}
                              <Button type="button" variant="text" size="small" onClick={() => addTreatment(index)}>
                                Add treatment
                              </Button>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              aria-label={
                                expandedVariantIndices.includes(index) ? 'Collapse treatments' : 'Expand treatments'
                              }
                              onClick={() => toggleVariantTreatments(index)}
                            >
                              <ExpandMoreIcon
                                sx={{
                                  transform: expandedVariantIndices.includes(index) ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: (theme) =>
                                    theme.transitions.create('transform', {
                                      duration: theme.transitions.duration.shortest,
                                    }),
                                }}
                              />
                            </IconButton>
                            <IconButton
                              aria-label={`Remove variant ${index + 1}`}
                              onClick={() => removeVariant(index)}
                              disabled={rule.variants.length === 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        {expandedVariantIndices.includes(index) ? (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ p: 0, bgcolor: 'background.default' }}>
                              {variants.treatments?.length ? (
                                <Box sx={{ width: '100%', p: 2 }}>
                                  <DataGridPro
                                    autoHeight
                                    disableRowSelectionOnClick
                                    hideFooter
                                    getRowId={(row) => row.id}
                                    rows={variants.treatments.map((treatment, treatmentIndex) => ({
                                      ...treatment,
                                      id: String(treatmentIndex),
                                    }))}
                                    columns={[
                                      {
                                        field: 'type',
                                        headerName: 'Type',
                                        flex: 0.9,
                                        sortable: false,
                                        renderCell: (params: GridRenderCellParams<TreatmentRow>) => {
                                          const summary = getTreatmentSummary(params.row);
                                          return summary.config?.typeName ?? params.row.type;
                                        },
                                      },
                                      {
                                        field: 'summary',
                                        headerName: 'Summary',
                                        flex: 1.8,
                                        sortable: false,
                                        renderCell: (params: GridRenderCellParams<TreatmentRow>) => {
                                          const { fields } = getTreatmentSummary(params.row);
                                          return fields.length ? fields.join(', ') : 'No fields set';
                                        },
                                      },
                                    ]}
                                    getDetailPanelContent={({ row }) => {
                                      const treatmentIndex = Number(row.id);
                                      const { config } = getTreatmentSummary(row);

                                      return (
                                        <Stack spacing={1} sx={{ p: 2, bgcolor: 'background.default' }}>
                                          <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={2}
                                            alignItems={{ sm: 'center' }}
                                          >
                                            <TextField
                                              label="Treatment type"
                                              select
                                              value={row.type}
                                              sx={{ flex: '1 1 220px' }}
                                              onChange={(event) => {
                                                const nextType = marketingRouterTreatmentTypes.find(
                                                  (type) => type === event.target.value,
                                                );

                                                if (nextType) {
                                                  updateTreatmentType(index, treatmentIndex, nextType);
                                                }
                                              }}
                                            >
                                              {marketingRouterTreatmentTypes.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                  {getMarketingRouterTreatmentConfig(type).typeName}
                                                </MenuItem>
                                              ))}
                                            </TextField>
                                            {config?.fields?.map((field) => (
                                              <TextField
                                                key={field.field}
                                                label={
                                                  field.required ? field.fieldName : `${field.fieldName} (optional)`
                                                }
                                                required={field.required}
                                                helperText={showFieldDescriptions ? field.longDescription : undefined}
                                                value={getTreatmentFieldValueForDraft(row, field.field)}
                                                sx={{ flex: '1 1 220px' }}
                                                onChange={(event) =>
                                                  updateTreatmentField(
                                                    index,
                                                    treatmentIndex,
                                                    field.field,
                                                    event.target.value,
                                                  )
                                                }
                                              />
                                            ))}
                                            <Button
                                              type="button"
                                              variant="text"
                                              color="error"
                                              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                                              onClick={() => removeTreatment(index, treatmentIndex)}
                                            >
                                              Remove
                                            </Button>
                                          </Stack>
                                          {showFieldDescriptions && config?.longDescription ? (
                                            <Typography color="text.secondary" variant="body2">
                                              {config.longDescription}
                                            </Typography>
                                          ) : null}
                                        </Stack>
                                      );
                                    }}
                                    getDetailPanelHeight={() => 'auto'}
                                    detailPanelExpandedRowIds={expandedTreatmentRowIds[index] ?? []}
                                    onDetailPanelExpandedRowIdsChange={(rowIds) =>
                                      handleExpandedTreatmentRowIdsChange(index, rowIds)
                                    }
                                    sx={{
                                      border: 1,
                                      borderColor: 'divider',
                                      '& .MuiDataGrid-cell': {
                                        alignItems: 'flex-start',
                                        py: 1,
                                      },
                                    }}
                                  />
                                </Box>
                              ) : (
                                <Box sx={{ p: 2 }}>
                                  <Typography color="text.secondary" variant="body2">
                                    No treatments configured
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
            {currentRuleErrors.length ? (
              <Alert severity="error" variant="outlined">
                <Stack spacing={1}>
                  <Typography variant="body2">Fix the following before saving:</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    {currentRuleErrors.map((error) => (
                      <Box component="li" key={error}>
                        {error}
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Alert>
            ) : null}
            <Stack direction="row" spacing={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSaving || !currentRuleCheck.canSave}
              >
                {isEditing ? 'Update rule' : 'Create rule'}
              </Button>
              <Button component={Link} to="/marketing/marketing-router-rules" variant="text">
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </>
  );
}
